"use client";

import { useCurrency } from "@/contexts/CurrencyContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { calculatePercentageDifference } from "@/utils/dealEvaluation";
import { isMobile } from "@/utils/helpers";
import { getThemeColors, hexToRgba } from "@/utils/styleHelpers";
import { FormControlLabel, Switch } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

interface PriceHistoryData {
  date: string;
  avgPrice?: number;
  soldAvgPrice?: number;
  count?: number;
  price?: number;
  isInitialListing?: boolean;
  isMarketAtListing?: boolean;
  isInterpolated?: boolean;
  isSoldImportantPoint?: boolean;
}

interface HistoricalPriceData {
  date: string;
  price?: number;
  avgPrice?: number;
  soldAvgPrice?: number;
  isInitialListing?: boolean;
  isMarketAtListing?: boolean;
  count?: number;
}

interface ActiveDotProps {
  cx: number;
  cy: number;
  payload: PriceHistoryData;
}

interface TooltipEntryProps {
  value: number;
  name: string;
  payload: PriceHistoryData;
}

enum ChartLabels {
  LISTING_PRICE = 'Listing Price',
  MARKET_AVERAGE = 'Market Average',
  SOLD_AVERAGE = 'Sold Average',
  INITIAL_LISTING_PRICE = 'Initial Listing Price: ',
  LISTING_PRICE_LABEL = 'Listing Price: ',
  MARKET_AVERAGE_AT_LISTING = 'Market Average (At Listing): ',
  CURRENT_MARKET_AVERAGE = 'Current Market Average: ',
  SOLD_MARKET_AVERAGE = 'Sold Market Average: '
}

interface AreaChartWithGradientProps {
  data: PriceHistoryData[];
  historicalData?: HistoricalPriceData[];
  similarCount?: number;
  soldCount?: number;
  compact?: boolean;
  className?: string;
}

const AreaChartWithGradient: React.FC<AreaChartWithGradientProps> = ({
  data,
  historicalData = [],
  similarCount = 0,
  soldCount = 0,
  compact = false,
  className = "",
}) => {
  const { formatPrice } = useCurrency();
  const { logEvent } = useAnalytics();
  const [showHistorical, setShowHistorical] = useState(true);
  const [processedData, setProcessedData] = useState<PriceHistoryData[]>([]);
  const [isChartReady, setIsChartReady] = useState(false);
  const [isSoldHovered, setIsSoldHovered] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isActiveListingsHovered, setIsActiveListingsHovered] = useState(false);
  const activeListingsTermRef = useRef<HTMLSpanElement>(null);
  const soldTermRef = useRef<HTMLSpanElement>(null);
  const [activeListingsTooltipPos, setActiveListingsTooltipPos] = useState({ left: '50%' });
  const [soldTooltipPos, setSoldTooltipPos] = useState({ left: '50%' });

  useEffect(() => {
    if (!compact) {
      setIsMobileDevice(isMobile());
    }
  }, [compact]);

  useEffect(() => {
    if (!compact && activeListingsTermRef.current) {
      const rect = activeListingsTermRef.current.getBoundingClientRect();
      const container = activeListingsTermRef.current.closest('.comparison-info-container');
      if (container) {
        const parentRect = container.getBoundingClientRect();
        const centerPos = ((rect.left + rect.width / 2) - parentRect.left);
        setActiveListingsTooltipPos({ left: `${centerPos}px` });
      }
    }
  }, [isActiveListingsHovered, compact]);

  useEffect(() => {
    if (!compact && isSoldHovered && soldTermRef.current) {
      const rect = soldTermRef.current.getBoundingClientRect();
      const container = soldTermRef.current.closest('.comparison-info-container');
      if (container) {
        const parentRect = container.getBoundingClientRect();
        const centerPos = ((rect.left + rect.width / 2) - parentRect.left);
        setSoldTooltipPos({ left: `${centerPos}px` });
      }
    }
  }, [isSoldHovered, compact]);

  const initialListingPoint = useMemo(() =>
    data.find(point => point.isInitialListing),
    [data]);

  const hasAvgPrice = useMemo(() =>
    data.some(item => item.avgPrice !== undefined),
    [data]);

  const hasEnoughHistoricalData = useMemo(() => {
    return historicalData && historicalData.length > 1;
  }, [historicalData]);

  useEffect(() => {
    if (showHistorical && !hasEnoughHistoricalData) {
      setShowHistorical(false);
    }
  }, [showHistorical, hasEnoughHistoricalData]);

  const toggleView = useCallback(() => {
    if (!hasEnoughHistoricalData && !showHistorical) {
      return;
    }

    const newViewState = !showHistorical;

    logEvent("chart_view_toggle", {
      chart_type: "price_history",
      old_view: showHistorical ? "historical" : "current",
      new_view: newViewState ? "historical" : "current",
      has_historical_data: hasEnoughHistoricalData,
      data_points: data.length,
      historical_points: historicalData.length,
      similar_count: similarCount,
      sold_count: soldCount
    });

    setIsChartReady(false);
    setShowHistorical(newViewState);
    setProcessedData([]);
  }, [showHistorical, hasEnoughHistoricalData, logEvent, data.length, historicalData.length, similarCount, soldCount]);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data]);

  const sortedHistoricalData = useMemo(() => {
    if (!historicalData || historicalData.length === 0) return [];
    return [...historicalData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [historicalData]);

  const enrichedSortedData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return [];
    if (!initialListingPoint) return sortedData;

    const listingDate = initialListingPoint.date;

    const postListingMarketData = sortedHistoricalData
      .filter(point =>
        point.date >= listingDate &&
        point.avgPrice !== undefined &&
        !sortedData.some(d => d.date === point.date && d.avgPrice !== undefined)
      );

    const enriched = [...sortedData];

    postListingMarketData.forEach(marketPoint => {
      const existingIndex = enriched.findIndex(p => p.date === marketPoint.date);
      if (existingIndex >= 0) {
        if (enriched[existingIndex].avgPrice === undefined) {
          enriched[existingIndex].avgPrice = marketPoint.avgPrice;
        }
      } else {
        enriched.push({
          date: marketPoint.date,
          avgPrice: marketPoint.avgPrice,
          count: 0
        });
      }
    });

    if (initialListingPoint) {
      const initialIndex = enriched.findIndex(p => p.date === initialListingPoint.date);
      if (initialIndex >= 0) {
        const nearestMarketDataPoint = sortedHistoricalData
          .filter(p => p.avgPrice !== undefined)
          .reduce<HistoricalPriceData | null>((nearest, current) => {
            if (!nearest) return current;

            const currentDiff = Math.abs(new Date(current.date).getTime() - new Date(initialListingPoint.date).getTime());
            const nearestDiff = Math.abs(new Date(nearest.date).getTime() - new Date(initialListingPoint.date).getTime());

            return currentDiff < nearestDiff ? current : nearest;
          }, null);

        if (nearestMarketDataPoint && !enriched[initialIndex].avgPrice) {
          enriched[initialIndex].avgPrice = nearestMarketDataPoint.avgPrice;
          enriched[initialIndex].isMarketAtListing = true;
        }
      }
    }

    return enriched.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [sortedData, sortedHistoricalData, initialListingPoint]);

  const activeDataSource = useMemo(() => {
    if (showHistorical && hasEnoughHistoricalData) {
      const combinedData: (HistoricalPriceData & { count?: number })[] = [...sortedHistoricalData.map(item => ({ ...item, count: 0 }))];

      sortedData.forEach(dataPoint => {
        if (!combinedData.some(h => h.date === dataPoint.date)) {
          combinedData.push(dataPoint);
        } else {
          const existingIndex = combinedData.findIndex(h => h.date === dataPoint.date);
          if (existingIndex >= 0) {
            combinedData[existingIndex] = {
              ...combinedData[existingIndex],
              ...dataPoint,
              isInitialListing: dataPoint.isInitialListing || combinedData[existingIndex].isInitialListing || false,
            };
          }
        }
      });

      return combinedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    return enrichedSortedData;
  }, [enrichedSortedData, sortedData, sortedHistoricalData, showHistorical, hasEnoughHistoricalData]);

  const fillDateGaps = (data: PriceHistoryData[]) => {
    if (data.length < 2) return data;

    const result = [data[0]];

    for (let i = 1; i < data.length; i++) {
      const currDate = new Date(data[i].date);
      const prevDate = new Date(data[i - 1].date);

      const daysDiff = Math.round((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff > 7) {
        const numPoints = Math.min(daysDiff - 1, 5);
        const interval = daysDiff / (numPoints + 1);

        for (let j = 1; j <= numPoints; j++) {
          const midDate = new Date(prevDate);
          midDate.setDate(prevDate.getDate() + Math.round(j * interval));

          const ratio = j / (numPoints + 1);

          const prevPrice = data[i - 1].price;
          const currPrice = data[i].price;
          const midPrice = prevPrice !== undefined && currPrice !== undefined
            ? prevPrice + (currPrice - prevPrice) * ratio
            : undefined;

          const prevAvg = data[i - 1].avgPrice;
          const currAvg = data[i].avgPrice;
          const midAvg = prevAvg !== undefined && currAvg !== undefined
            ? prevAvg + (currAvg - prevAvg) * ratio
            : undefined;

          result.push({
            date: midDate.toISOString().split('T')[0],
            price: midPrice,
            avgPrice: midAvg,
            isInterpolated: true,
            count: 0
          });
        }
      }

      result.push(data[i]);
    }

    return result;
  };

  useEffect(() => {
    const prepareData = () => {
      setIsChartReady(false);

      if (!activeDataSource || activeDataSource.length === 0) {
        const today = new Date();
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);

        return [
          {
            date: lastMonth.toISOString().split('T')[0],
            price: 1000,
            avgPrice: 1100,
            count: 0
          },
          {
            date: today.toISOString().split('T')[0],
            price: 1000,
            avgPrice: 1100,
            count: 0
          }
        ];
      }

      if (activeDataSource.length === 1) {
        const point = activeDataSource[0];
        const date = new Date(point.date);
        const newDate = new Date(date);
        newDate.setDate(date.getDate() + 7);

        return [
          { ...point },
          {
            ...point,
            date: newDate.toISOString().split('T')[0],
          }
        ];
      }

      let normalizedData = activeDataSource.map(point => {
        let dateStr = point.date;
        if (!(typeof dateStr === 'string')) {
          const date = new Date(point.date);
          dateStr = date.toISOString().split('T')[0];
        }

        return {
          ...point,
          date: dateStr,
          price: point.price !== undefined ? Number(point.price) : undefined,
          avgPrice: point.avgPrice !== undefined ? Number(point.avgPrice) : undefined,
          soldAvgPrice: point.soldAvgPrice !== undefined ? Number(point.soldAvgPrice) : undefined,
          isInitialListing: point.isInitialListing || false,
          isMarketAtListing: point.isMarketAtListing || false,
          count: point.count || 0
        };
      });

      normalizedData = normalizedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      if (!showHistorical) {
        const marketDataPoints = normalizedData.filter(point => point.avgPrice !== undefined);

        if (marketDataPoints.length === 1) {
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];

          const existingTodayPoint = normalizedData.find(p => p.date === todayStr);

          if (existingTodayPoint) {
            existingTodayPoint.avgPrice = marketDataPoints[0].avgPrice;
          } else {
            normalizedData.push({
              date: todayStr,
              avgPrice: marketDataPoints[0].avgPrice,
              count: 0,
              price: undefined,
              soldAvgPrice: undefined,
              isInitialListing: false,
              isMarketAtListing: false
            });

            normalizedData = normalizedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          }
        }

        if (initialListingPoint) {
          const initialListingIndex = normalizedData.findIndex(
            p => p.isInitialListing || p.date === initialListingPoint.date
          );

          if (initialListingIndex >= 0 && normalizedData[initialListingIndex].avgPrice !== undefined) {
            normalizedData[initialListingIndex].isMarketAtListing = true;
          }
        }
      }

      const filledData = fillDateGaps(normalizedData);

      return filledData.map(item => ({
        ...item,
        count: item.count || 0
      }));
    };

    const processedResult = prepareData();
    setProcessedData(processedResult);

    const timer = setTimeout(() => {
      setIsChartReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [activeDataSource, showHistorical, initialListingPoint]);

  const hasSoldPrice = useMemo(() =>
    data.some(item => item.soldAvgPrice !== undefined),
    [data]);

  const themeColors = getThemeColors();

  const colors = {
    itemPrice: {
      stroke: themeColors.frenchBeige,
      fill: themeColors.frenchBeige,
      gradient: [hexToRgba(themeColors.frenchBeige, 0.6), hexToRgba(themeColors.frenchBeige, 0.05)]
    },
    marketAverage: {
      stroke: themeColors.sageGreen,
      fill: themeColors.sageGreen,
      gradient: [hexToRgba(themeColors.sageGreen, 0.65), hexToRgba(themeColors.sageGreen, 0)]
    },
    soldAverage: {
      stroke: themeColors.maroon,
      fill: themeColors.maroon,
      gradient: [hexToRgba(themeColors.maroon, 0.65), hexToRgba(themeColors.maroon, 0.08)]
    },
    initialListing: themeColors.frenchBeige,
    grid: themeColors.lightGrey,
    text: themeColors.darkGrey,
  };

  const formatDateAxis = (dateStr: string) => {
    if (!dateStr) return '';
    const dateObj = new Date(dateStr);
    return `${dateObj.getDate()} ${dateObj.toLocaleString('en-US', { month: 'short' })}`;
  };

  const hasValidData = processedData && processedData.length >= 2;

  const renderAreaComponents = () => {
    const lastDataPoint = processedData[processedData.length - 1];
    if (!lastDataPoint) return null;

    const areaConfigs = [];

    if (lastDataPoint.price !== undefined && lastDataPoint.price !== null) {
      areaConfigs.push({
        dataKey: 'price',
        name: ChartLabels.LISTING_PRICE,
        color: colors.itemPrice,
        value: lastDataPoint.price || 0,
        fillOpacity: 1.0,
        strokeWidth: 1
      });
    }

    if (hasAvgPrice && lastDataPoint.avgPrice !== undefined && lastDataPoint.avgPrice !== null) {
      areaConfigs.push({
        dataKey: 'avgPrice',
        name: ChartLabels.MARKET_AVERAGE,
        color: colors.marketAverage,
        value: lastDataPoint.avgPrice || 0,
        fillOpacity: 1.0,
        strokeWidth: 1
      });
    }

    if (hasSoldPrice && lastDataPoint.soldAvgPrice !== undefined && lastDataPoint.soldAvgPrice !== null) {
      areaConfigs.push({
        dataKey: 'soldAvgPrice',
        name: ChartLabels.SOLD_AVERAGE,
        color: colors.soldAverage,
        value: lastDataPoint.soldAvgPrice || 0,
        fillOpacity: 1.0,
        strokeWidth: 1
      });
    }

    areaConfigs.sort((a, b) => b.value - a.value);

    return areaConfigs.map(config => {
      if (config.dataKey === 'price') {
        return (
          <Area
            key={config.dataKey}
            type="monotone"
            dataKey={config.dataKey}
            name={config.name}
            stroke={config.color.stroke}
            strokeWidth={config.strokeWidth}
            fillOpacity={config.fillOpacity}
            fill={`url(#color${config.dataKey.charAt(0).toUpperCase() + config.dataKey.slice(1)})`}
            dot={false}
            activeDot={(props: ActiveDotProps) => {
              if (!props.payload || !props.payload.price) return <></>;

              return (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={6}
                  stroke={config.color.stroke}
                  fill={config.color.fill}
                  strokeWidth={1}
                />
              );
            }}
            connectNulls={true}
            isAnimationActive={true}
            animationDuration={1000}
            animationEasing="ease-in-out"
          />
        );
      } else if (config.dataKey === 'avgPrice') {
        return (
          <Area
            key={config.dataKey}
            type="monotone"
            dataKey={config.dataKey}
            name={config.name}
            stroke={config.color.stroke}
            strokeWidth={config.strokeWidth}
            fillOpacity={config.fillOpacity}
            fill={`url(#color${config.dataKey.charAt(0).toUpperCase() + config.dataKey.slice(1)})`}
            dot={false}
            activeDot={(props: ActiveDotProps) => {
              if (!props.payload || !props.payload.avgPrice) return <></>;

              return (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={5}
                  stroke={config.color.stroke}
                  fill={config.color.fill}
                  strokeWidth={1}
                />
              );
            }}
            connectNulls={true}
            isAnimationActive={true}
            animationDuration={1000}
            animationEasing="ease-in-out"
          />
        );
      } else if (config.dataKey === 'soldAvgPrice') {
        return (
          <Area
            key={config.dataKey}
            type="monotone"
            dataKey={config.dataKey}
            name={config.name}
            stroke={config.color.stroke}
            strokeWidth={config.strokeWidth}
            fillOpacity={config.fillOpacity}
            fill={`url(#color${config.dataKey.charAt(0).toUpperCase() + config.dataKey.slice(1)})`}
            dot={false}
            activeDot={(props: ActiveDotProps) => {
              if (!props.payload || !props.payload.soldAvgPrice) return <></>;

              return (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={5}
                  stroke={config.color.stroke}
                  fill={config.color.fill}
                  strokeWidth={1}
                />
              );
            }}
            connectNulls={true}
            isAnimationActive={true}
            animationDuration={1000}
            animationEasing="ease-in-out"
          />
        );
      }

      return null;
    });
  };

  const getMarketDifferences = () => {
    const lastPoint = processedData[processedData.length - 1];

    if (!lastPoint) return { marketDiff: null, soldDiff: null, marketDollarDiff: null, soldDollarDiff: null };

    let marketDiff = null;
    let soldDiff = null;
    let marketDollarDiff = null;
    let soldDollarDiff = null;

    if (lastPoint.price !== undefined && lastPoint.price !== null &&
      lastPoint.avgPrice !== undefined && lastPoint.avgPrice !== null) {
      const result = calculatePercentageDifference(lastPoint.price, lastPoint.avgPrice, 1);
      if (result) {
        marketDiff = result.percentDifference;
        marketDollarDiff = result.dollarDifference;
      }
    }

    if (lastPoint.price !== undefined && lastPoint.price !== null &&
      lastPoint.soldAvgPrice !== undefined && lastPoint.soldAvgPrice !== null) {
      const result = calculatePercentageDifference(lastPoint.price, lastPoint.soldAvgPrice, 1);
      if (result) {
        soldDiff = result.percentDifference;
        soldDollarDiff = result.dollarDifference;
      }
    }

    return { marketDiff, soldDiff, marketDollarDiff, soldDollarDiff };
  };

  if (!hasValidData || !isChartReady) {
    if (compact) {
      return (
        <div className={`bg-white h-[120px] flex items-center justify-center ${className}`}>
          <div className="text-gray-400 text-sm">Loading chart...</div>
        </div>
      );
    }
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="bg-white pb-0">
      {!compact && (
        <div className="flex justify-between items-center ml-2.5 mb-1 pb-2.5 border-b border-gray-100">
          {(hasAvgPrice || hasSoldPrice) && (similarCount > 0 || soldCount > 0) && hasEnoughHistoricalData && (
            <FormControlLabel
              control={
                <Switch
                  checked={showHistorical}
                  onChange={toggleView}
                  color="primary"
                  size="small"
                  disabled={!hasEnoughHistoricalData}
                />
              }
              label={
                <span className="text-xs text-gray-600 font-medium">
                  {showHistorical ? "Historical View" : "Listing View"}
                </span>
              }
              className="mt-2.5 text-sm"
            />
          )}
        </div>
      )}

      {!compact && (hasAvgPrice || hasSoldPrice) && (similarCount > 0 || soldCount > 0) && (
        <div className="relative mb-4 hidden md:block">
          <div className={`ml-2 w-full font-raleway ${isMobileDevice ? 'text-xs' : 'text-sm'}`}>
            {(() => {
              const { marketDiff, soldDiff, marketDollarDiff, soldDollarDiff } = getMarketDifferences();
              return (
                <>
                  {soldDollarDiff !== null && soldCount > 0 && hasSoldPrice && soldDiff !== null && (
                    <>
                      <span className="text-black font-normal">We estimate this listing is </span>
                      <span className="text-maroon font-bold">{formatPrice(Math.abs(soldDollarDiff))}</span>
                      <span className="text-black font-normal">, or </span>
                      <span className="text-maroon font-bold">{Math.abs(soldDiff).toFixed(1)}%</span>
                      <span className="text-black font-bold"> {soldDiff >= 0 ? 'above' : 'below'}</span>
                      <span className="text-black font-normal"> the </span>
                      <span className="text-maroon font-normal">Sold Market Average</span>
                      <span className="text-black font-normal"> based on </span>
                      <span
                        ref={soldTermRef}
                        className="text-black font-normal underline"
                        onMouseEnter={() => {
                          setIsSoldHovered(true);
                          logEvent("chart_tooltip_hover", {
                            chart_type: "price_history",
                            tooltip_type: "sold_items",
                            sold_count: soldCount
                          });
                        }}
                        onMouseLeave={() => setIsSoldHovered(false)}
                      >
                        {soldCount} sold {soldCount === 1 ? 'item' : 'items'}
                      </span>
                    </>
                  )}

                  {marketDollarDiff !== null && hasAvgPrice && similarCount > 0 && marketDiff !== null && (
                    <>
                      {soldDiff !== null ? (
                        <span className="text-black font-normal"> and </span>
                      ) : (
                        <span className="text-black font-normal">We estimate this is </span>
                      )}
                      <span className="text-sage-green font-bold">{formatPrice(Math.abs(marketDollarDiff))}</span>
                      <span className="text-black font-normal">, or </span>
                      <span className="text-sage-green font-bold">{Math.abs(marketDiff).toFixed(1)}%</span>
                      <span className="text-black font-bold"> {marketDiff >= 0 ? 'above' : 'below'}</span>
                      <span className="text-black font-normal"> the </span>
                      <span className="text-sage-green font-normal">Current Market Average</span>
                      <span className="text-black font-normal"> based on </span>
                      <span
                        ref={activeListingsTermRef}
                        className="text-black font-normal underline"
                        onMouseEnter={() => {
                          setIsActiveListingsHovered(true);
                          logEvent("chart_tooltip_hover", {
                            chart_type: "price_history",
                            tooltip_type: "active_listings",
                            similar_count: similarCount
                          });
                        }}
                        onMouseLeave={() => setIsActiveListingsHovered(false)}
                      >
                        {similarCount} active {similarCount === 1 ? 'listing' : 'listings'}
                      </span>
                    </>
                  )}
                  <span className="text-black font-normal">.</span>
                </>
              );
            })()}
          </div>

          {isActiveListingsHovered && (
            <div
              className="absolute top-full left-1/2 transform -translate-x-1/2 bg-white border border-dark-grey font-raleway text-xs p-3 max-w-xs shadow-lg mt-1 whitespace-normal text-black"
              style={activeListingsTooltipPos}
            >
              Currently active listings with matching specifications
            </div>
          )}

          {isSoldHovered && (
            <div
              className="absolute top-full left-1/2 transform -translate-x-1/2 bg-white border border-dark-grey font-raleway text-xs p-3 max-w-xs shadow-lg mt-1 whitespace-normal text-black"
              style={soldTooltipPos}
            >
              Recently sold items with matching specifications
            </div>
          )}
        </div>
      )}

      <div className={`w-full ${compact ? 'h-[120px]' : 'h-80'} [&_*]:outline-none [&_*]:focus:outline-none ${className}`}>
        {hasValidData && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={processedData}
              margin={compact ? { top: 0, right: -30, left: 0, bottom: 0 } : { top: 15, right: -20, left: -20, bottom: 25 }}
              className={`transition-opacity duration-300 ${isChartReady ? 'opacity-100' : 'opacity-50'}`}
            >
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.itemPrice.gradient[0]} />
                  <stop offset="85%" stopColor={colors.itemPrice.gradient[1]} />
                </linearGradient>
                {hasAvgPrice && (
                  <linearGradient id="colorAvgPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.marketAverage.gradient[0]} />
                    <stop offset="85%" stopColor={colors.marketAverage.gradient[1]} />
                  </linearGradient>
                )}
                {hasSoldPrice && (
                  <linearGradient id="colorSoldAvgPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.soldAverage.gradient[0]} />
                    <stop offset="85%" stopColor={colors.soldAverage.gradient[1]} />
                  </linearGradient>
                )}
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={formatDateAxis}
                tick={{ fontSize: 10, fill: compact ? colors.text : '#666' }}
                tickMargin={10}
                padding={compact ? { left: 0, right: 20 } : { left: 30, right: 30 }}
                stroke={compact ? 'transparent' : '#666'}
                type="category"
                tickLine={false}
                axisLine={compact ? false : undefined}
                domain={['dataMin', 'dataMax']}
              />
              <YAxis
                hide={true}
                axisLine={false}
                tickLine={false}
              // domain={['dataMin', 'dataMax']}
              />
              <CartesianGrid
                strokeDasharray="0"
                stroke={colors.grid}
                strokeWidth={0.75}
                strokeOpacity={undefined}
                horizontal={false}
                vertical={true}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length || !label) return null;

                  const formatDate = (dateStr: string) => {
                    const dateObj = new Date(dateStr);
                    return `${dateObj.getDate()} ${dateObj.toLocaleString('en-US', { month: 'long' })} ${dateObj.getFullYear()}`;
                  };

                  return (
                    <div className={`bg-white p-3 border border-dark-grey font-raleway text-xs flex flex-col ${compact ? 'gap-2' : 'gap-3'} shadow-lg}`}>
                      <div className="text-black font-medium">
                        {formatDate(String(label))}
                      </div>
                      {payload.map((entry: TooltipEntryProps, index: number) => {
                        if (!entry.value) return null;

                        let labelText = '';
                        const labelColor = 'text-dark-grey';
                        let valueColor = 'text-french-beige';

                        if (entry.name === ChartLabels.LISTING_PRICE) {
                          if (entry.payload.isInitialListing) {
                            labelText = ChartLabels.INITIAL_LISTING_PRICE;
                          } else {
                            labelText = ChartLabels.LISTING_PRICE_LABEL;
                          }
                          valueColor = 'text-french-beige';
                        } else if (entry.name === ChartLabels.MARKET_AVERAGE) {
                          if (entry.payload.isMarketAtListing) {
                            labelText = ChartLabels.MARKET_AVERAGE_AT_LISTING;
                          } else {
                            labelText = ChartLabels.CURRENT_MARKET_AVERAGE;
                          }
                          valueColor = 'text-sage-green';
                        } else if (entry.name === ChartLabels.SOLD_AVERAGE) {
                          labelText = ChartLabels.SOLD_MARKET_AVERAGE;
                          valueColor = 'text-maroon';
                        }

                        return (
                          <div key={index} className="text-left">
                            <span className={`font-normal ${labelColor}`}>{labelText}</span>
                            <span className={`font-bold ${valueColor}`}>{formatPrice(entry.value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />

              {initialListingPoint && (
                <ReferenceLine
                  x={initialListingPoint.date}
                  stroke={colors.initialListing}
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                />
              )}

              {hasValidData && renderAreaComponents()}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {!compact && !hasAvgPrice && (
        <p className="text-sm text-gray-500 italic text-center mt-2 mb-0">
          No similar items found for price comparison
        </p>
      )}
    </div>
  );
};

export default AreaChartWithGradient;